/**
 * This file is responsible for loading essential configuration
 * values at runtime.
 */

class RunTimeConfig {
  constructor() {
    this.configs = null
    this.fetchPromise = null
    this.error = null
  }

  /**
   * Loads runtime configuration from the backend API endpoint.
   * This method ensures configuration is fetched only once per session
   * and handles loading states and error scenarios gracefully.
   * @returns {Promise<void>} A promise that resolves when the configuration has been fetched.
   */
  async fetchConfigs() {
    if (this.configs) {
      return
    }

    if (this.fetchPromise) {
      return this.fetchPromise
    }

    this.fetchPromise = (async () => {
      this.error = null
      try {
        const response = await fetch('/runtime-configs', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) {
          throw new Error(
            `Failed to load runtime config: ${response.status} ${response.statusText}`,
          )
        }

        this.configs = await response.json()
      } catch (error) {
        this.error = error
        console.error('Failed to load application configuration:', error)
        this.configs = {}
      }
    })()

    return this.fetchPromise
  }

  /**
   * Gets a specific configuration value
   * @param {string} key - Configuration key
   * @param {*} defaultValue - Default value if key not found
   * @returns {*} Configuration value
   */
  get(key, defaultValue = '') {
    if (!this.configs) {
      console.warn(`Configuration not loaded yet. Cannot get value for key: ${key}`)
      return defaultValue
    }
    return this.configs[key] || defaultValue
  }

  /**
   * Resets the configuration state (useful for testing)
   */
  reset() {
    this.configs = null
    this.fetchPromise = null
    this.error = null
  }
}

// Export a singleton instance
export default new RunTimeConfig()
